import { delay } from 'es-toolkit/promise';

const NETWORK_RETRY_DELAY_MS = 1000;

function uploadBodyOnce(
  body: File | ArrayBuffer,
  contentType: string,
  uploadUrl: string,
  onProgress?: (percent: number) => void,
): Promise<Error | null> {
  return new Promise<Error | null>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(null);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      resolve(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(body);
  });
}

async function uploadBodyWithProgress(
  body: File | ArrayBuffer,
  contentType: string,
  uploadUrl: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const networkError = await uploadBodyOnce(
    body,
    contentType,
    uploadUrl,
    onProgress,
  );
  if (!networkError) {
    return;
  }

  await delay(NETWORK_RETRY_DELAY_MS);

  const retryError = await uploadBodyOnce(
    body,
    contentType,
    uploadUrl,
    onProgress,
  );
  if (retryError) {
    throw retryError;
  }
}

export function uploadFileWithProgress(
  file: File,
  uploadUrl: string,
  onProgress: (percent: number) => void,
): Promise<void> {
  return uploadBodyWithProgress(
    file,
    file.type || 'application/octet-stream',
    uploadUrl,
    onProgress,
  );
}

export function uploadArrayBufferWithProgress(
  data: ArrayBuffer | Uint8Array,
  uploadUrl: string,
  onProgress?: (percent: number) => void,
  contentType = 'application/octet-stream',
): Promise<void> {
  let body: ArrayBuffer;

  if (data instanceof Uint8Array) {
    const buffer = data.buffer;
    const isArrayBuffer = buffer instanceof ArrayBuffer;
    const isFullView =
      data.byteOffset === 0 && data.byteLength === buffer.byteLength;

    if (isArrayBuffer && isFullView) {
      body = buffer;
    } else {
      body = data.slice().buffer;
    }
  } else {
    body = data;
  }

  return uploadBodyWithProgress(body, contentType, uploadUrl, onProgress);
}
